import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { clearToken } from '../../auth/token';
import { useTheme } from '../../context/ThemeContext';

export default function Sidebar() {
  const [moneyOpen, setMoneyOpen] = useState(false);
  const [habitsOpen, setHabitsOpen] = useState(false);
  const navigate = useNavigate();
  const { darkMode, toggleTheme } = useTheme();

  const handleLogout = () => {
    clearToken();
    navigate('/login');
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm font-medium transition-all duration-200 ${
      isActive
        ? 'bg-brand-500 text-white shadow-md shadow-brand-500/30'
        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50'
    }`;

  const subLinkClass = ({ isActive }) =>
    `flex items-center gap-2 px-4 py-2 rounded-xl text-sm transition-all duration-200 ${
      isActive
        ? 'bg-brand-50 dark:bg-brand-900/40 text-brand-700 dark:text-brand-400 font-medium'
        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
    }`;

  const toggleIcon = (open) => (
    <svg
      className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );

  return (
    <aside className="w-64 h-screen sticky top-0 flex flex-col border-r border-border-light dark:border-border-dark bg-white/80 dark:bg-slate-800/80 backdrop-blur-lg">
      {/* Logo / Brand */}
      <div className="p-6">
        <h1 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
          My<span className="text-brand-500">App</span>
        </h1>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        <NavLink to="/dashboard" className={linkClass}>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          Dashboard
        </NavLink>

        <NavLink to="/profile" className={linkClass}>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Profile
        </NavLink>

        {/* Money Control */}
        <div>
          <button
            onClick={() => setMoneyOpen(!moneyOpen)}
            className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded-2xl transition-all duration-200"
          >
            <span className="flex items-center gap-3">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Money Control
            </span>
            {toggleIcon(moneyOpen)}
          </button>
          {moneyOpen && (
            <div className="ml-5 mt-1 space-y-0.5 border-l border-border-light dark:border-border-dark pl-3">
              <NavLink to="/money/dashboard" className={subLinkClass}>Overview</NavLink>
              <NavLink to="/money/transactions" className={subLinkClass}>Transactions</NavLink>
              <NavLink to="/money/reports" className={subLinkClass}>Reports</NavLink>
              <NavLink to="/money/categories" className={subLinkClass}>Categories</NavLink>
              <NavLink to="/money/savings" className={subLinkClass}>Savings</NavLink>
            </div>
          )}
        </div>

        {/* Daily Routine */}
        <div>
          <button
            onClick={() => setHabitsOpen(!habitsOpen)}
            className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded-2xl transition-all duration-200"
          >
            <span className="flex items-center gap-3">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              Daily Routine
            </span>
            {toggleIcon(habitsOpen)}
          </button>
          {habitsOpen && (
            <div className="ml-5 mt-1 space-y-0.5 border-l border-border-light dark:border-border-dark pl-3">
              <NavLink to="/daily/today" className={subLinkClass}>Today</NavLink>
              <NavLink to="/daily/report" className={subLinkClass}>Monthly Report</NavLink>
              <NavLink to="/daily/habits" className={subLinkClass}>My Habits</NavLink>
            </div>
          )}
        </div>
          <NavLink to="/reminders" className={linkClass}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            Reminders
          </NavLink>

          <NavLink to="/notifications" className={linkClass}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            Notifications
          </NavLink>
      </nav>

      {/* Theme Toggle */}
      <div className="px-3 py-2 border-t border-border-light dark:border-border-dark">
        <div className="flex items-center justify-between px-4 py-2">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {darkMode ? 'Dark Mode' : 'Light Mode'}
          </span>
          <button
            onClick={toggleTheme}
            className="relative w-11 h-6 rounded-full bg-slate-300 dark:bg-slate-600 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-brand-400"
            aria-label="Toggle theme"
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-300 ${
                darkMode ? 'translate-x-5' : ''
              }`}
            />
          </button>
        </div>
      </div>

      {/* Logout */}
      <div className="p-3 border-t border-border-light dark:border-border-dark">
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-2xl transition-all duration-200"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Logout
        </button>
      </div>
    </aside>
  );
}